const mediaErrorKey = (error) => {
  if (['NotAllowedError', 'SecurityError'].includes(error?.name)) return 'permissionDenied';
  if (['NotFoundError', 'OverconstrainedError'].includes(error?.name)) return 'devicesUnavailable';
  if (['AbortError', 'NotReadableError'].includes(error?.name)) return 'deviceDisconnected';
  return 'previewFailed';
};

const requestUserMedia = constraints => (navigator.mediaDevices?.getUserMedia
  ? navigator.mediaDevices.getUserMedia(constraints)
  : new Promise((resolve, reject) => {
    if (navigator.webkitGetUserMedia) navigator.webkitGetUserMedia(constraints, resolve, reject);
    else reject(new Error('Media devices unavailable'));
  }));

// This controller, and only this controller, owns physical capture tracks.
export const createGuidedMediaDevices = ({ onState }) => {
  const devices = navigator.mediaDevices;
  let generation = 0;
  let enumerationGeneration = 0;
  let disposed = false;
  let trackListeners = [];
  const state = {
    stream: null,
    videoDevices: [],
    audioDevices: [],
    selectedVideoDeviceId: '',
    selectedAudioDeviceId: '',
    requesting: false,
    errorKey: '',
    noiseSuppressionSupported: devices?.getSupportedConstraints?.().noiseSuppression === true,
    noiseSuppressionApplied: null,
  };
  const emit = () => { if (!disposed) onState({ ...state }); };
  const detach = () => {
    trackListeners.forEach(([track, listener]) => track.removeEventListener('ended', listener));
    trackListeners = [];
    state.stream?.getTracks().forEach(track => track.stop());
    state.stream = null;
    state.noiseSuppressionApplied = null;
  };
  const stop = () => {
    generation += 1;
    detach();
    state.requesting = false;
    emit();
  };
  const refreshDevices = async () => {
    const version = ++enumerationGeneration;
    try {
      const list = devices?.enumerateDevices ? await devices.enumerateDevices() : [];
      if (disposed || version !== enumerationGeneration) return;
      const options = kind => list.filter(device => device.kind === kind).map((device, index) => ({
        deviceId: device.deviceId,
        label: device.label,
        fallbackKey: kind === 'videoinput' ? 'camera' : 'microphone',
        index: index + 1,
      }));
      state.videoDevices = options('videoinput');
      state.audioDevices = options('audioinput');
      emit();
    } catch {
      // Enumeration is optional. Redacted/empty lists must never prevent permission acquisition.
      if (!disposed && version === enumerationGeneration) emit();
    }
  };
  const interrupt = () => {
    stop();
    state.errorKey = 'deviceDisconnected';
    emit();
    void refreshDevices();
  };
  const onDeviceChange = async () => {
    await refreshDevices();
    if (!state.stream || disposed) return;
    const missing = (kind, selected, options) => selected && !options.some(option => option.deviceId === selected)
      && state.stream.getTracks().some(track => track.kind === kind);
    if (missing('video', state.selectedVideoDeviceId, state.videoDevices)
      || missing('audio', state.selectedAudioDeviceId, state.audioDevices)) interrupt();
  };
  devices?.addEventListener?.('devicechange', onDeviceChange);
  const start = async ({ videoDeviceId = '', audioDeviceId = '', noiseSuppression = state.noiseSuppressionSupported } = {}) => {
    if (disposed) return null;
    stop();
    const version = generation;
    state.selectedVideoDeviceId = videoDeviceId;
    state.selectedAudioDeviceId = audioDeviceId;
    state.errorKey = '';
    state.requesting = true;
    emit();
    const audio = audioDeviceId ? { deviceId: { exact: audioDeviceId } } : {};
    if (state.noiseSuppressionSupported) audio.noiseSuppression = { ideal: Boolean(noiseSuppression) };
    try {
      let stream;
      try {
        stream = await requestUserMedia({
          video: videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true,
          audio: Object.keys(audio).length ? audio : true,
        });
      } catch (error) {
        // A browser rejecting the optional suppression constraint may still capture normally.
        if (disposed || version !== generation) return null;
        if (!audio.noiseSuppression || !['OverconstrainedError', 'NotSupportedError'].includes(error?.name)
          || (error.constraint && error.constraint !== 'noiseSuppression')) throw error;
        delete audio.noiseSuppression;
        stream = await requestUserMedia({
          video: videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true,
          audio: Object.keys(audio).length ? audio : true,
        });
      }
      if (disposed || version !== generation) {
        stream.getTracks().forEach(track => track.stop());
        return null;
      }
      state.stream = stream;
      const videoTrack = stream.getVideoTracks()[0];
      const audioTrack = stream.getAudioTracks()[0];
      state.selectedVideoDeviceId = videoTrack?.getSettings?.().deviceId || videoDeviceId;
      state.selectedAudioDeviceId = audioTrack?.getSettings?.().deviceId || audioDeviceId;
      const applied = audioTrack?.getSettings?.().noiseSuppression;
      state.noiseSuppressionApplied = typeof applied === 'boolean' ? applied : null;
      stream.getTracks().forEach((track) => {
        track.addEventListener('ended', interrupt);
        trackListeners.push([track, interrupt]);
      });
      await refreshDevices();
      return stream;
    } catch (error) {
      if (!disposed && version === generation) state.errorKey = mediaErrorKey(error);
      return null;
    } finally {
      if (!disposed && version === generation) {
        state.requesting = false;
        emit();
      }
    }
  };
  emit();
  return {
    start,
    stop,
    refreshDevices,
    dispose() {
      if (disposed) return;
      stop();
      disposed = true;
      enumerationGeneration += 1;
      devices?.removeEventListener?.('devicechange', onDeviceChange);
    },
  };
};
